import { BLOCKS } from '@contentful/rich-text-types'
import {
  AssetOptionsContext,
  type AssetOptionsContextProps,
  DEFAULT_MAX_SVG_HEIGHT,
  DEFAULT_MIN_SVG_WIDTH,
} from '@contexts/asset-options'
import { ContentType } from '@entries/content-types'
import ContentfulAsset from '@entries/contentful-asset'
import { withDisplayName } from '@hocs/with-display-name'
import { heading2Gutter } from '@render-node/heading-2-gutter'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import { contentArticleImageTextOptions } from '@rich-text/contentful-content-article-rich-text'
import { Hyperlink } from '@ui/hyperlink'
import { clsx } from 'clsx'
import { graphql } from 'gatsby'
import React from 'react'

const assetOptions: AssetOptionsContextProps = {
  width: 'auto',
  height: 'auto',
  minSvgWidth: DEFAULT_MIN_SVG_WIDTH,
  maxSvgHeight: DEFAULT_MAX_SVG_HEIGHT,
  objectFit: 'contain',
  alt: '',
}

const ContentfulContentArticleRecommendation: Utils.EntryFC<
  Queries.ContentfulContentArticleRecommendationFragment
> = ({
  content: {
    contentLinkText,
    contentLinkUrl,
    contentArticleTitle: title,
    subtitle,
    image,
    imageText,
    content,
  },
  entryInspectorProps,
  fieldInspectorProps,
}) => {
  const hasFooter = Boolean(contentLinkText && contentLinkUrl)
  const hasImage = imageText || image

  return (
    <div className="flex w-full flex-col" {...entryInspectorProps()}>
      {title && (
        <h3
          className="border-b-none m-0 w-auto rounded-b-[0px] rounded-t-[4px] border border-border-default bg-theme-default-accent2 px-8 py-2 text-085 font-normal uppercase text-text-default laptop:w-fit"
          {...fieldInspectorProps({
            fieldId: 'title',
          })}
        >
          {title.text}
        </h3>
      )}
      <div
        className={clsx(
          'flex h-full w-full flex-col rounded-md border border-solid border-theme-default-accent2 bg-background-default text-text-default',
          'rounded-b-[4px]',
          title
            ? 'rounded-tl-[0px] rounded-tr-[0px] laptopL:rounded-tr-[4px]'
            : 'rounded-t-[4px]'
        )}
      >
        <div className="flex h-full flex-col justify-between p-10">
          <div>
            <div className="flex flex-col-reverse items-center gap-5 laptop:flex-row">
              <div>
                {subtitle && (
                  <RichText
                    {...fieldInspectorProps({
                      fieldId: 'subtitle',
                    })}
                    content={subtitle}
                    options={defaultRichTextFieldOptionsDeprecated}
                  />
                )}
                {content && (
                  <RichText
                    {...fieldInspectorProps({
                      fieldId: 'content',
                    })}
                    content={content}
                    options={{
                      ...defaultRichTextFieldOptionsDeprecated,
                      renderNode: {
                        ...defaultRichTextFieldOptionsDeprecated.renderNode,
                        [BLOCKS.HEADING_2]: heading2Gutter,
                      },
                    }}
                  />
                )}
              </div>
              {hasImage && (
                <div className="flex min-w-[200px] items-start justify-center tablet:max-w-[280px]">
                  {image && (
                    <AssetOptionsContext.Provider value={assetOptions}>
                      <Entry
                        content={image}
                        mapping={{
                          [ContentType.ContentfulAsset]: ContentfulAsset,
                        }}
                      />
                    </AssetOptionsContext.Provider>
                  )}
                  {imageText && (
                    <RichText
                      {...fieldInspectorProps({
                        fieldId: 'imageText',
                      })}
                      content={imageText}
                      options={contentArticleImageTextOptions}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          {hasFooter && (
            <div className="pt-10">
              <Hyperlink
                {...fieldInspectorProps({
                  fieldId: 'contentLinkText',
                })}
                href={contentLinkUrl}
              >
                {contentLinkText}
              </Hyperlink>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const query = graphql`
  fragment ContentfulContentArticleRecommendation on Contentful4TmD9EihHakmKMkEwKaUwQ {
    ...ContentfulEntry
    contentArticleTitle: title {
      ...ContentfulContentArticleTitle
    }
    subtitle {
      ...ContentfulContentArticleSubtitle
    }
    content {
      ...ContentfulContentArticleContent
    }
    image {
      ...ContentfulDefaultAsset
    }
    imageText {
      ...ContentfulContentArticleImageText
    }
    contentLinkText
    contentLinkUrl
  }
`

export default withDisplayName(
  ContentfulContentArticleRecommendation,
  'ContentfulContentArticleRecommendation'
)
